import { LICENSE_MODULE } from "@selector/shared";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { LoadingSpinner, TriggerButton } from "widgets";

import { CELL_TYPE } from "@/settings/settings.ts";
import { useAsyncFilter } from "@/ui-modules/core/forms/hooks/useAsyncFilter.js";

import { matchFilter, parseFilterExpression } from "../../app-utils/filter.js";
import { type Configuration, type Playbook } from "../../graphql.js";
import { isLicensed } from "../../redux/modules/config.js";
import { loadAdminPlaybooks } from "../../redux/modules/playbook-admin.js";
import { type IIdentifiable } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import DataContextIndicator from "../core/grid/DataContextIndicator.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import PlaybookConfigWizard from "../playbooks/editor/PlaybookConfigWizard.js";

const adminPlaybooksColumnDefs = [
  {
    name: "Name",
    field: "playbook.name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Suffix",
    field: "playbook.nameSuffix",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Description",
    field: "playbook.description",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Categories",
    field: "playbook.categories.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
  {
    name: "Type",
    field: "playbook.type.name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Roles",
    field: "playbook.roles.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
  {
    name: "Status",
    field: "playbook.enabled",
    cellType: CELL_TYPE.BOOLEAN,
  },
];

const AdminPlaybooksTable = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [playbookToEdit, setPlaybookToEdit] = useState<Playbook | null>(null);
  const [rowData, setRowData] = useState<IIdentifiable[]>([]);
  const [showAddPlaybookDialog, setShowAddPlaybookDialog] = useState(false);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  const playbooks = useSelector<IAppState, Playbook[]>((state) => state.playbookAdminState.adminPlaybooks);
  const isLoadingPlaybooks = useSelector<IAppState>((state) => state.playbookAdminState.isLoadingAdminPlaybooks);
  const [filterTerm, setFilterTerm] = useAsyncFilter("");

  const handlePlaybookClicked = (playbook: Playbook) => {
    setPlaybookToEdit(playbook);
    setShowAddPlaybookDialog(true);
  };

  const cellRendererParams = {
    "playbook.name": {
      handlePlaybookClicked: handlePlaybookClicked,
    },
  };

  useEffect(() => {
    dispatch(loadAdminPlaybooks());
  }, [dispatch]);

  useEffect(() => {
    setRowData(createPlaybookRowData(filterTerm, playbooks));
  }, [playbooks, filterTerm]);

  const createPlaybooksDisabledMessage =
    !isLicensed(configuration, LICENSE_MODULE.TRACKER) && !isLicensed(configuration, LICENSE_MODULE.PROCESSOR)
      ? "Creating custom Playbooks is only possible with Tracker and/or Processor license."
      : null;

  return (
    <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
      <PageHeader
        title={"Playbooks"}
        badge={String(playbooks.length)}
        toolsWidgets={[
          <AsyncSearchWithExprParser
            onSearchExprChange={setFilterTerm}
            controlledValue={filterTerm}
            key={"playbooks-admin-page-widget-1"}
          />,
        ]}
        actionWidgets={[
          <TriggerButton
            key={"playbooks-admin-page-action-1"}
            isDisabled={!!createPlaybooksDisabledMessage}
            icon={"queue_play_next"}
            tooltip={"Create a new playbook"}
            onPress={() => {
              setPlaybookToEdit(null);
              setShowAddPlaybookDialog(true);
            }}
            label={"New Playbook"}
            showLabel
          />,
        ]}
      />
      {isLoadingPlaybooks ? (
        <LoadingSpinner inCenter />
      ) : (
        <div style={{ height: "calc(100% - 50px)" }}>
          <InMemoryTableWithSelectionModel
            columns={adminPlaybooksColumnDefs}
            rowGridData={rowData}
            useSelectionModel={false}
            uniqueGridIdentifier={"admin-playbooks"}
            cellRendererParamsLookup={cellRendererParams}
          />
          <DataContextIndicator
            key="context-indicator"
            numTotalRows={playbooks.length}
            numRowsFiltered={rowData?.length}
            rowEntityLabel={"Playbook"}
            filterIsSet={!!filterTerm?.length}
            onResetFilter={() => setFilterTerm("")}
          />
        </div>
      )}

      {showAddPlaybookDialog && (
        <PlaybookConfigWizard initialPlaybookId={playbookToEdit?.id} onClose={() => setShowAddPlaybookDialog(false)} />
      )}
    </div>
  );
};

const createPlaybookRowData = (filterTerm: string, playbooks: Playbook[]): IIdentifiable[] => {
  return playbooks
    .filter((playbook: Playbook) =>
      matchFilter(
        [
          playbook.name,
          playbook.description,
          playbook.type.name,
          playbook.categories.flatMap((category) => category.name).join(","),
          playbook.roles?.flatMap((role) => role?.name).join(","),
        ],
        parseFilterExpression(filterTerm),
      ),
    )
    .map((playbook: Playbook) => {
      return {
        id: playbook.id,
        playbook: playbook,
      };
    });
};

export { AdminPlaybooksTable };
